import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Text, Link, TextCollapse, Spacing } from '@reservamos/elements';
import { formatCoverageAmount } from 'utils/currency';
import ModalPopUp from '../../../ui/atoms/ModalPopUp';
import DisclaimerText from '../DisclaimerText';
import 'styles/components/payment/Insurance';
import useInsuranceConfig from '../../../hooks/useInsuranceConfig';

/**
 * Component to render multiple links based on object URL.
 * @param {object} props - href
 * @param {Array<{text: string, url: string}>} props.href - Array of url objects to show as links
 * @returns {Array<JSX.Element>} - Link components
 */
const MultiLink = ({ href }) => {
  return href.map(({ text, url }) => (
    <Link key={url} text={text} href={url} type="primary" fontFamily="secondary" newTab size="S" />
  ));
};

MultiLink.propTypes = {
  href: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

/**
 * ModalContentTripInsurance component.
 * Render insurance modal with coverage details and T&C links.
 *
 * @param {object} props - message, onAccept, onReject.
 * @param {string} props.message - Informational message for the modal.
 * @param {Function} props.onAccept - Handler for primary action.
 * @param {Function} props.onReject - Handler for secondary action.
 */
const ModalContentTripInsurance = ({ message, onAccept, onReject }) => {
  const { icon, detailsUrl, coverage, disclaimerConfig } = useInsuranceConfig();
  const coverageItems = coverage?.coverageItems ?? [];
  const { t } = useTranslation();

  return (
    <ModalPopUp
      modalTitle={t('passengers:label.add_coverage')}
      modalIconType={icon}
      buttonPrimaryText={t('passengers:button.yes_thanks_coverage')}
      buttonSecondaryText={t('passengers:button.no_thanks')}
      onClickPrimary={onAccept}
      onClickSecondary={onReject}
    >
      <Text fontFamily="secondary" size="S">
        {message}
      </Text>
      {coverageItems.length > 0 && (
        <TextCollapse
          triggerTextCollapsed={t('see_details')}
          triggerTextExpanded={t('close_details')}
          triggerOptions={{ size: 'S', type: 'discount' }}
          margin="XS"
          triggerPosition="top"
          hasChevron
        >
          <Spacing vertical size="XS" alignItems="flex-start">
            {coverageItems.map((item) => (
              <Spacing
                key={item.id ? `${item.key}-${item.id}` : item.key}
                alignItems="center"
                size="XS"
              >
                <Text size="S">•</Text>
                <Text size="S">
                  {t(`insurance:${item.key}`)}
                  {item.amount && `: ${formatCoverageAmount(item.amount)}`}
                </Text>
              </Spacing>
            ))}
            {typeof detailsUrl === 'string' && (
              <Link
                text={t('insurance:terms_conditions')}
                href={detailsUrl}
                type="primary"
                fontFamily="secondary"
                newTab
                size="S"
              />
            )}
            {Array.isArray(detailsUrl) && <MultiLink href={detailsUrl} />}
            {disclaimerConfig && (
              <Text size="XS" fontFamily="secondary" color="grayMedium">
                <DisclaimerText config={disclaimerConfig} />
              </Text>
            )}
          </Spacing>
        </TextCollapse>
      )}

      {typeof detailsUrl === 'string' && coverageItems.length === 0 && (
        <Text fontFamily="secondary" size="S">
          <Link
            text={t('insurance:terms_conditions')}
            href={detailsUrl}
            type="primary"
            fontFamily="secondary"
            newTab
          />
          .
          {disclaimerConfig && (
            <>
              {' '}
              <Text size="XS" fontFamily="secondary" color="grayMedium">
                <DisclaimerText config={disclaimerConfig} />
              </Text>
            </>
          )}
        </Text>
      )}
    </ModalPopUp>
  );
};

ModalContentTripInsurance.propTypes = {
  message: PropTypes.string.isRequired,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
};

export default ModalContentTripInsurance;
